import React from 'react';
import moment from 'moment';
import { nfd } from 'unorm';
import { uniq, flatten } from 'lodash';
import { Popup } from 'semantic-ui-react';
import { createAPI } from './api';

import { __ } from './i18n';
import config from '../../config/server.config';

/**
 * @typedef {string} ID
 * @typedef {'ADMIN' | 'STAFF' | 'STUDENT'} EntityType
 * @typedef {'LIGHT' | 'STANDARD' | 'PREMIUM' | 'STANDARD_NEW' | 'LIGHT_NEW' | 'FREE'} OrganizationPlan
 */

/**
 * Cast to a valid URL.
 * @param {string} url
 */
export const castToValidURL = (url) => {
  const re = /^(http|https):\/\//i;
  const match = re.test(url);

  if (!match) {
    return 'http://' + url;
  }

  const isHttp = /^(http):\/\//i.test(url);

  return url.replace(re, isHttp ? 'http://' : 'https://');
};

/**
 * Formater date.
 * @param {string} date - The date string.
 * @param {string} format - The format date.
 */
export const dateFormat = (date, format) => {
  const formattedDate = moment.utc(date).format(format);
  return formattedDate !== 'Invalid date' ? formattedDate : null;
};

/**
 * Format date to locale.
 * @param {string} locale
 */
export const adaptLocaleToMoment = (locale) => {
  /**
   * @type {{
   *  [lang: string]: string | undefined,
   * }}
   */
  const momentLocale = {
    pt: 'pt-br',
    es: 'es',
    zh: 'zh-cn',
  };

  return momentLocale[locale] ? momentLocale[locale] : locale;
};

/**
 * Simple date formatter
 * @param {string} date
 * @param {boolean} time
 * @param {string | null} format
 * @param {string} locale
 * @returns {moment.Moment | string}
 */
export const simpleDate = (date, time = true, format = null, locale = 'en') => {
  const now = moment();
  let lang = locale;

  if (!lang) lang = 'en';

  if (lang.length > 2) lang = lang.substring(0, 2);

  const dict = {
    pt: 'pt-BR',
    en: 'en',
    es: 'es',
    ja: 'ja',
    zh: 'zh',
  };

  if (lang === 'es') {
    moment.updateLocale('es', {
      monthsShort: [
        'Ene.',
        'Feb.',
        'Mar.',
        'Abr.',
        'Mayo',
        'Jun.',
        'Jul.',
        'Ago.',
        'Sept.',
        'Oct.',
        'Nov.',
        'Dic.',
      ],
    });
  }

  // @ts-ignore
  moment.locale(adaptLocaleToMoment(dict[lang]));
  let then = moment(date);
  const diff = then.diff(now, 'hours');

  then = then.utcOffset(now.utcOffset());

  if (format) {
    return then.format(format);
  }

  if (diff > -12 && diff < 12 && time) {
    return then.format('LT');
  }
  if (diff > -4380 && diff < 4380) {
    //24 * 365/2
    return then.format('MMM D');
  }

  return then.format('L');
};

/**
 * Adjust Timezone of Date
 * @param {Date} date
 * @param {number} tzOffset
 * @returns {Date}
 */
export const adjustTZDate = (date, tzOffset) =>
  new Date(date.getTime() - (tzOffset || date.getTimezoneOffset()) * 60000);

/**
 *
 * @returns {Object}
 */
// @ts-ignore
export const objectFilter = (obj, predicate) =>
  Object.assign(
    {},
    ...Object.keys(obj)
      .filter((key) => predicate(obj[key]))
      .map((key) => ({ [key]: obj[key] })),
  );

/**
 * Same as `map`, but waits for the previous mapped element to finish before mapping the next
 * @template T
 * @template U
 * @param  {(T | Promise<T>)[]} promisesOrVals Array of promises or values
 * @param  {(item: T, index: number, array: (T | Promise<T>)[]) => U} mapper The mapper function
 */
export const mapSeries = (promisesOrVals, mapper) =>
  promisesOrVals.reduce(
    // @ts-ignore
    (prevResults, pOrV, i, array) =>
      prevResults.then((results) =>
        Promise.resolve(pOrV)
          .then((value) => mapper(value, i, array))
          // @ts-ignore
          .then((result) => results.concat([result])),
      ),
    Promise.resolve([]),
  );

/**
 * Location query parser
 * @param {string[] | boolean[]} query
 * @returns {string[] | boolean[]}
 */
export const parseQuery = (query) => {
  for (const i in query) {
    if (query[i] === 'true') {
      query[i] = true;
    } else if (query[i] === 'false') {
      query[i] = false;
    }
  }
  return query;
};

/**
 * Normalize text
 * @param {string} string
 * @returns {string | null}
 */
export const normalize = (string) => {
  if (!string) return null;

  return nfd(string).replace(/[\u0300-\u036f]/g, '');
};

/**
 * @param {string} path
 * @param {'production' | 'beta' | 'stage' | 'internal' | 'local'} [env]
 *
 * If using a custom asset URL from config.assets, an env is needed
 * @param {string} path
 * @param {string} customUrl
 * @param {'production' | 'beta' | 'stage' | 'internal' | 'local' | undefined} env
 */
export const asset = (path, customUrl, env) => {
  if (customUrl && config.assets[customUrl]) {
    // @ts-ignore
    if (config.assets[customUrl][env]) {
      // @ts-ignore
      return config.assets[customUrl][env] + path;
    }

    return '/public/' + customUrl + path;
  }

  // @ts-ignore
  const isProd = ['production', 'stage', 'internal', 'beta'].includes(
    // @ts-ignore
    process.env.NODE_ENV,
  );
  const devServerURL = isProd ? config.assets.cdn.media : '/public';
  return devServerURL + path;
};

/**
 * Check if string is a valid URL
 * @param {string} url
 * @returns {boolean}
 */
export const isUrl = (url) => {
  if (!url) return false;
  const regexp =
    /^(?:(?:https?|ftp):\/\/)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:\/\S*)?$/;
  return (
    url.toLowerCase().trim().split(' ').length === 1 &&
    !!regexp.test(url.toLowerCase())
  );
};

/**
 *
 * @param {string} text
 */
export const parseText = (text) =>
  text
    .split(' ')
    .map((value) =>
      value
        .split(/\n/)
        .map((v) => {
          if (!isUrl(v)) return v;

          return `<a target="_blank" rel="noreferrer noopener" href="${v.search('://') === -1 ? 'http://' + v : v}">${v}</a>`;
        })
        .join('\n'),
    )
    .join(' ');

/**
 * Parse string style to object
 * @param {string} styleText
 */
export const parseStyle = (styleText) => {
  /**
   * @type {string[]}
   */
  const attrs = styleText.split(';');
  /**
   * @type {{ [key: string]: string }}
   */
  const parsed_attrs = {};

  attrs.forEach((attr) => {
    if (!attr.length) return;

    const attribute = attr.split(':');
    const key = attribute[0]
      .split('-')
      .map((a, i) => (i ? a.replace(/\b\w/g, (l) => l.toUpperCase()) : a))
      .join('')
      .trim();

    parsed_attrs[key] = attribute[1].trim();
  });

  return parsed_attrs;
};

/**
 * Transform location query object to URL encoded params
 * @param {string[]} query
 * @returns {string}
 */
export const stringify = (query) => {
  let string = '';

  Object.keys(query).forEach((key) => {
    // @ts-ignore
    if (Array.isArray(query[key])) {
      // @ts-ignore
      string += `&${query[key].map((value, i) => `${key}[${i}]=${value}`).join('&')}`;
    } else {
      // @ts-ignore
      string += `&${key}=${query[key]}`;
    }
  });

  return string;
};

/**
 * Transform GraphQL error to a translated message
 * @param {{ message: string }} error
 * @param {string | undefined} source
 * @returns {string}
 */
export const handleError = (error, source = '') => {
  const defaultErrorMessage =
    error && error.message ? error.message.toString() : error.toString();
  let finalErrorMessage = defaultErrorMessage;

  switch (defaultErrorMessage) {
    case 'Not authorized':
      finalErrorMessage = __('Not authorized');
      break;
    case 'Not allowed':
      finalErrorMessage = __('Not allowed');
      break;
    case 'Error':
      finalErrorMessage = __(
      'Error register a new bank account. Please verify the information provided.',
    );
      break;
    case 'Internal Server Error':
      finalErrorMessage = __(
      'An error occured trying to perform the operation. Please verify the information provided.',
    );
      break;
    case 'Sending messages is disabled':
      finalErrorMessage = __('Sending messages is disabled');
      break;
    case 'Replies are disabled':
      finalErrorMessage = __('Replies are disabled');
      break;
    case 'Entity is disabled':
      finalErrorMessage = __('Entity is disabled');
      break;
    case 'Action has expired':
      finalErrorMessage = __('Action has expired');
      break;

    case 'Message must not be empty':
      finalErrorMessage = __('Message must not be empty');
      break;
    case 'No recipients defined':
      finalErrorMessage = __('No recipients defined');
      break;

    case 'Address code not found':
      finalErrorMessage = __('Address code not found');
      break;
    case 'Address code already in use':
      finalErrorMessage = __('Address code already in use');
      break;

    case 'Code not found':
      finalErrorMessage = __('Code not found');
      break;
    case 'Code has expired':
      finalErrorMessage = __('Code has expired');
      break;

    case 'Token not found':
      finalErrorMessage = __('Token not found');
      break;

    case 'Email not found':
      finalErrorMessage = __(
      'Email address was not found, try entering your phone number or contact the organization to be invited',
    );
      break;
    case 'Email already in use':
      finalErrorMessage = __('Email already in use');
      break;
    case 'Email already confirmed':
      finalErrorMessage = __('Email already confirmed');
      break;

    case 'Phone not found':
      finalErrorMessage = __(
      'Phone number was not found, try entering your email or contact the organization to be invited',
    );
      break;
    case 'Phone already in use':
      finalErrorMessage = __('Phone already in use');
      break;
    case 'Phone already confirmed':
      finalErrorMessage = __('Phone already confirmed');
      break;

    case 'Email or phone not found':
      finalErrorMessage = __('Email or phone not found');
      break;
    case 'Email or phone not confirmed':
      finalErrorMessage = __('Email or phone not confirmed');
      break;
    case 'Email or phone not defined':
      finalErrorMessage = __('Email or phone not defined');
      break;

    case 'Password does not match':
      finalErrorMessage = __('Password does not match');
      break;
    case 'Current password must match':
      finalErrorMessage = __('Current password must match');
      break;
    case 'Wrong user/password combination':
      finalErrorMessage = __('Wrong user/password combination');
      break;

    case 'Network error: Network request failed':
      finalErrorMessage = __('Check your internet connection');
      break;
    case 'Entity already exists':
      finalErrorMessage = __(
      'You cannot have people or channels with the same name and ID',
    );
      break;

    case 'Fullname is required on creating':
      finalErrorMessage = __('Fullname is required on creating');
      break;

    case 'Group\'s name already exist':
      finalErrorMessage = __('Group\'s name already exist');
      break;

    case '{"address":["Validation for address did not pass"]}':
      finalErrorMessage = __('Validation for address did not pass');
      break;

    case 'Invalid code':
      finalErrorMessage = __('Invalid code');
      break;

    case 'Node not found':
      if (source === 'codesignup') {
        finalErrorMessage = __('Code not found');
      } else {
        finalErrorMessage = __('Resource not found');
      }
      break;

    case '{"fullname":["The fullname is required"]}':
      finalErrorMessage = __('Fullname cannot be empty');
      break;

    case 'A message can\'t be forwarded more than once':
      finalErrorMessage = __('A message can\'t be forwarded more than once');
      break;

    case 'Duplicated addresses are not allowed':
      finalErrorMessage = __('Duplicated addresses are not allowed');
      break;

    case 'Username already in use':
      finalErrorMessage = __('Username already in use');
      break;

    case '400 - {"status":"400","msg":"Campo agencia_repasse está inválido. Valide as informações preenchidas e tente novamente. OBS: A agência deve conter 4 números + DV (um dígito)."}':
      finalErrorMessage = __(
      'Agency field is invalid. Validate the information you have entered and try again. NOTE: The agency must contain 4 numbers + DV (one digit).',
    );
      break;

    case '400 - {"status":"400","msg":"Agência de repasse inválida."}':
      finalErrorMessage = __('Invalid agency value.');
      break;

    case '400 - {"status":"400","msg":"CPF/CNPJ inválido."}':
      finalErrorMessage = __('Invalid document number.');
      break;

    case '400 - {"status":"400","msg":"CNPJ inválido."}':
      finalErrorMessage = __('Invalid document number.');
      break;

    case '400 - {"status":"400","msg":"Conta bancária para repasse inválida."}':
      finalErrorMessage = __('Invalid current account number.');
      break;

    case '{"phone":["Validation for phone did not pass"]}':
      finalErrorMessage = __('Invalid phone number.');
      break;

    case '{"email":["The email must be a valid email address"]}':
      finalErrorMessage = __('Invalid email');
      break;

    case '400 - {"status":"400","msg":"Token do cliente não encontrado."}':
      finalErrorMessage = __('Client token not found.');
      break;

    case '400 - {"status":"400","msg":"Nome da empresa inválido."}':
      finalErrorMessage = __('Institution name must be at least 3 characters');
      break;

    case 'Cannot perform any action in deleted messages':
      finalErrorMessage = __('Cannot perform any action in deleted messages');
      break;

    case 'Cannot perform any action in deleted replies':
      finalErrorMessage = __('Cannot perform any action in deleted replies');
      break;

    case 'Users of this charge have already been reminded today':
      finalErrorMessage = __(
      'A reminder has already been sent for this charge today',
    );
      break;

    case 'Users have already been reminded today':
      finalErrorMessage = __(
      'A reminder has already been sent for this survey today',
    );
      break;

    case 'Group already exists':
      finalErrorMessage = __('Group already exists');
      break;

    case 'Bank account check digit is required.':
      finalErrorMessage = __('Bank account check digit is required.');
      break;

    case 'Address not found':
      finalErrorMessage = __('Code not found');
      break;

    case 'User already has this entity':
      finalErrorMessage = __('User already has this entity');
      break;

    case 'The recipient could not be created.':
      finalErrorMessage = __('The recipient could not be created.');
      break;
    case 'Card creation limit exceeded. Current: 5. Maximum: 5':
      finalErrorMessage = __('You have reached the card creation limit');
      break;
    case 'The amount must be greater than or equal to 100':
      finalErrorMessage = __(
      'The withdrawal amount must be greater than or equal to R$1,00',
    );
      break;
    case 'Account doesn\'t have available balance to withdraw the specified amount':
      finalErrorMessage = __(
      "Account doesn't have available balance to withdraw the specified amount",
    );
      break;
    case 'This charge can not be canceled.':
      finalErrorMessage = __('It was not possible to refund this payment');
      break;
    case 'Card already exists':
      finalErrorMessage = __('Card already exists');
      break;
    case 'Net withdrawal must be greater than or equal to 1':
      finalErrorMessage = __(
      'Net withdrawal must be greater than or equal to R$1,00',
    );
      break;
    case 'Network error: Failed to fetch':
      finalErrorMessage = __('Network error: Failed to fetch');
      break;
    case 'This email is already being used':
      finalErrorMessage = __('This email is already being used');
      break;
    case 'Application ID Error':
      finalErrorMessage = __('Application ID was not provided');
      break;
    case 'link must be a valid url':
      finalErrorMessage = __('Link must be a valid URL');
      break;
    case 'Tag with this name already exists':
      finalErrorMessage = __('Tag with this name already exists');
      break;
    case 'Application access already exists!':
      finalErrorMessage = __('Integration already exists!');
      break;
    case 'Label already exists':
      finalErrorMessage = __('Label already exists');
      break;
    case 'There is an active contract':
      finalErrorMessage = __('There is an active contract');
      break;
    case 'Fee must be greater than or equal to 0':
      finalErrorMessage = __('Fee must be greater than or equal to 0');
      break;
    case 'Application access not exists':
      finalErrorMessage = __('Application access not exists');
      break;
    case 'An error occurred while processing the signature':
      finalErrorMessage = __(
      'An error occurred while processing the signature',
    );
      break;
    case 'Form already answered':
      finalErrorMessage = __('Form already answered');
      break;
    default:
      break;
  }

  return finalErrorMessage;
};

/**
 * Format bytes string
 * @param {number} a
 * @param {number} b
 * @returns {number | string}
 */
export const formatBytes = (a, b = 2) => {
  if (a == 0) return '0 Bytes';

  const c = 1024;
  const d = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const e = Math.floor(Math.log(a) / Math.log(c));

  return parseFloat((a / c ** e).toFixed(b)) + ' ' + d[e];
};

/**
 * Convert base 64 to blob
 * @param {string} b64Data
 * @param {string} contentType
 * @param {number} sliceSize
 * @returns {Blob}
 */
export const b64toBlob = (b64Data, contentType, sliceSize) => {
  // eslint-disable-next-line no-param-reassign
  contentType = contentType || '';
  // eslint-disable-next-line no-param-reassign
  sliceSize = sliceSize || 512;

  const byteCharacters = atob(b64Data);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);

    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);

    byteArrays.push(byteArray);
  }

  return new Blob(byteArrays, { type: contentType });
};

/**
 * Merge two arrays
 * @param {string[]} array1
 * @param {string[]} array2
 * @returns
 */
export const intersection = (array1, array2) =>
  array1.filter((n) => array2.indexOf(n) !== -1);

/**
 * Redirect a HTTP URL to HTTPS
 * @param {string} url
 */
export const redirectUrl = (url) => {
  if (typeof window !== 'undefined') {
    const link = url.indexOf('://') === -1 ? 'http://' + url : url;
    // @ts-ignore
    window.location = link;
  }
};

/**
 * Round minutes to 30 or 00 minutes
 * @param {Date} date
 * @returns {Date}
 */
export const roundDate = (date) => {
  const minutes = date.getMinutes();

  if (!minutes || minutes === 30) return date;

  if (minutes < 30) {
    date.setMinutes(30);
  } else {
    date.setHours(date.getHours() + 1);
    date.setMinutes(0);
  }

  return date;
};

/**
 * Check if it is client or school
 * @param {string} type
 * @returns {number}
 */
export const checkIfItIsClientOrSchool = (type) => {
  if (
    !type ||
    type.toLowerCase().indexOf('school') > -1 ||
    type.toLowerCase().indexOf('education') > -1 ||
    type.toLowerCase().indexOf('teaching') > -1 ||
    type.toLowerCase() === 'extracurricular' ||
    type.toLowerCase() === 'de' ||
    type.toLowerCase() === 'ballet' ||
    type.toLowerCase() === 'soccer' ||
    type.toLowerCase() === 'technical_course' ||
    type.toLowerCase() === 'ngo' ||
    type.toLowerCase() === 'code'
  ) {
    return 1;
  }
  return 0;
};

/**
 *
 * @param {string} firstNineDigits
 * @returns {number}
 */
const calcFirstChecker = (firstNineDigits) => {
  /**
   * @type {number}
   */
  let sum = 0;

  for (let j = 0; j < 9; ++j) {
    sum += Number(firstNineDigits.toString().charAt(j)) * (10 - j);
  }

  const lastSumChecker1 = sum % 11;
  return lastSumChecker1 < 2 ? 0 : 11 - lastSumChecker1;
};

/**
 *
 * @param {string} cpfWithChecker1
 * @returns
 */
const calcSecondChecker = (cpfWithChecker1) => {
  /**
   * @type {number}
   */
  let sum = 0;

  for (let k = 0; k < 10; ++k) {
    sum += Number(cpfWithChecker1.toString().charAt(k)) * (11 - k);
  }

  const lastSumChecker2 = sum % 11;
  return lastSumChecker2 < 2 ? 0 : 11 - lastSumChecker2;
};

/**
 *
 * @param {string | null | undefined} str
 * @returns {string | undefined}
 */
export const stripHtml = (str) => {
  if (str) return str.replace(/<(?:.|\n)*?>/gm, '');
  return undefined;
};

/**
 * Verify if it is a valid email
 * @param {string} value
 * @returns {boolean}
 */
export const validateEmail = (value) => {
  if (
    value.includes('..') ||
    !value.match(
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/,
    )
  )
    return false;
  return true;
};

/**
 * Verify if it is a valid CPF
 * @param {string | number} value
 * @returns {string | boolean}
 */
export const validateCPF = (value) => {
  if (typeof value !== 'string' && typeof value !== 'number') {
    console.warn('Unsupported value of CPF');
    return false;
  }

  const cleanCPF = String(value).replace(/\.|-|\s/g, '');
  const firstNineDigits = cleanCPF.substring(0, 9);
  const checker = cleanCPF.substring(9, 11);

  if (cleanCPF.length !== 11) {
    return false;
  }

  // Checking if all digits are equal
  for (let i = 0; i < 10; i++) {
    if (`${firstNineDigits}${checker}` === Array(12).join(String(i))) {
      return false;
    }
  }

  const checker1 = calcFirstChecker(firstNineDigits);
  const checker2 = calcSecondChecker(`${firstNineDigits}${checker1}`);

  return checker.toString() === checker1.toString() + checker2.toString();
};

/**
 * Verify if it is a valid CNPJ
 * @param {string} cnpj
 * @returns {*}
 */
export const validateCNPJ = (cnpj) => {
  const cleanCNPJ = cnpj.replace(/[^\d]+/g, '');

  if (cleanCNPJ == '') return false;

  if (cleanCNPJ.length != 14) {
    return false;
  }

  if (
    cleanCNPJ == '00000000000000' ||
    cleanCNPJ == '11111111111111' ||
    cleanCNPJ == '22222222222222' ||
    cleanCNPJ == '33333333333333' ||
    cleanCNPJ == '44444444444444' ||
    cleanCNPJ == '55555555555555' ||
    cleanCNPJ == '66666666666666' ||
    cleanCNPJ == '77777777777777' ||
    cleanCNPJ == '88888888888888' ||
    cleanCNPJ == '99999999999999'
  ) {
    return false;
  }

  let length = cleanCNPJ.length - 2;
  let numbers = cleanCNPJ.substring(0, length);
  const digits = cleanCNPJ.substring(length);
  /**
   * @type {number}
   */
  let sum = 0;
  let pos = length - 7;

  for (let i = length; i >= 1; i--) {
    // @ts-ignore
    sum += numbers.charAt(length - i) * pos--;

    if (pos < 2) {
      pos = 9;
    }
  }

  let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);

  // @ts-ignore
  if (result != digits.charAt(0)) {
    return false;
  }

  length += 1;
  numbers = cleanCNPJ.substring(0, length);
  sum = 0;
  pos = length - 7;
  for (let i = length; i >= 1; i--) {
    // @ts-ignore
    sum += numbers.charAt(length - i) * pos--;

    if (pos < 2) {
      pos = 9;
    }
  }
  result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  // @ts-ignore
  if (result != digits.charAt(1)) {
    return false;
  }

  return true;
};

/**
 * Verify if it is a valid document CNPJ
 * @param {string} document
 * @returns {boolean}
 */
export const isDocumentCNPJ = (document) => {
  if (!document) return false;
  const cleanCNPJ = document.replace(/[^\d]+/g, '');
  return cleanCNPJ.length === 14;
};

/**
 * Verify if it is a valid image file
 * @param {string} mimetype
 * @returns {boolean}
 */
export const isImageFile = (mimetype) => !!mimetype.match('image');

/**
 * Verify if it is a valid image file
 * @param {string} mimetype
 * @param {string} filename
 * @returns {boolean}
 */
export const isAudioFile = (mimetype, filename) =>
  !!mimetype.match('audio') ||
  !!filename.match(/\.(mp3|m4A|aac|ogg|oga|wav|wave|ac3|aiff|flac|opus|wma)$/i);

/**
 * Verify if it is a valid image file
 * @param {string} mimetype
 * @param {string} filename
 * @returns {boolean}
 */
export const isVideoFile = (mimetype, filename) =>
  !isAudioFile(mimetype, filename) && mimetype.includes('video');

/**
 * Verify if it is a valid image file
 * @param {string} mimetype
 * @param {string} filename
 * @returns {boolean}
 */
export const isAudioIcon = (mimetype, filename) =>
  !!mimetype.match('audio') ||
  !!filename.match(/\.(mp3|m4A|aac|ogg|oga|wav|wave|ac3|aiff|flac|opus|wma)$/i);

/**
 * Verify if it is a valid image file
 * @param {string} filename
 * @returns {boolean}
 */
export const isPdfFile = (filename) =>
  !!filename.toLowerCase().match(/\.(pdf)$/i);

/**
 * Verify if it is a valid image file
 * @param {string} mimeType
 * @param {string} filename
 */
export const getAttachmentIcon = (mimeType, filename) => {
  if (isImageFile(mimeType)) return 'image';
  if (isVideoFile(mimeType, filename)) return 'video';
  if (isAudioIcon(mimeType, filename)) return 'volume up';
  // if (isAudioFile(mimeType, filename)) return 'volume up';
  if (filename.match(/\.(pdf)$/i)) return 'file pdf outline';
  if (filename.match(/\.(doc|docx|odt)$/i)) return 'file word outline';
  if (filename.match(/\.(xls|xlsx|ods)$/i)) return 'file excel outline';
  if (filename.match(/\.(ppt|pptx|odp)$/i)) return 'file powerpoint outline';
  if (filename.match(/\.(rar|zip|tar\.gz|7z)$/i)) return 'file archive outline';
  return 'file outline';
};

/**
 * Get media type
 * @param {string} mimetype
 * @param {string} filename
 * @returns {string}
 */
export const getMediaType = (mimetype, filename) => {
  let mediaType = 'FILE';
  if (mimetype.match('image')) mediaType = 'IMAGE';
  else if (isVideoFile(mimetype, filename)) mediaType = 'VIDEO';
  else if (isAudioFile(mimetype, filename)) mediaType = 'AUDIO';
  return mediaType;
};

/**
 * Filter entities by types and usages
 * @param {{
 *  currentUser?: {
 *    entities: {
 *      __typename: string,
 *      type: 'ADMIN' | 'STAFF' | 'STUDENT',
 *      organization: {
 *        id: number | string,
 *        usage: string,
 *      },
 *    }[],
 *  },
 * }} store
 * @param {string[]} withTypes
 * @param {string[]} withUsages
 * @param {string[]} withoutUsages
 * @param {(number | string)[]} withoutOrgIds
 * @returns {boolean}
 */
export const hasEntity = (
  store,
  withTypes,
  withUsages,
  withoutUsages,
  withoutOrgIds,
) => {
  if (!store.currentUser || !store.currentUser.entities) return false;

  let entities = store.currentUser.entities.filter(
    (e) => e.__typename === 'Entity',
  );

  entities = entities.filter((e) => !withTypes || withTypes.includes(e.type));
  entities = entities.filter(
    (e) =>
      !withUsages ||
      !e.organization ||
      withUsages.includes(e.organization.usage),
  );
  entities = entities.filter(
    (e) =>
      !withoutUsages ||
      !e.organization ||
      !withoutUsages.includes(e.organization.usage),
  );
  entities = entities.filter(
    (e) =>
      !withoutOrgIds ||
      !e.organization ||
      !withoutOrgIds.includes(e.organization.id),
  );
  return entities.length > 0;
};

/**
 * Verify if user has entity
 * @param {{ entity: { id: number }, currentUser: { entities: {  id: number, code: number  }[] }}} store
 * @returns {boolean}
 */
export const userHasEntity = (store) => {
  const targetId = store.entity && store.entity.id;
  const userEntities = store.currentUser.entities;
  if (!targetId || !userEntities) return false;
  return userEntities.some(
    (entity) => entity.code && entity.id && entity.id === targetId,
  );
};

/**
 * Verify if has entity in current organization
 * @param {{
 *  currentOrganization: { id: number} ,
 *  currentUser: {
 *    entities: {
 *      __typename: string, type: ('ADMIN'|'STAFF'|'STUDENT'),
 *      organization: { id: number, usage: string }
 *    }[]
 *  }}} store
 * @param {string[]} withTypes
 * @param {string[]} withUsages
 * @param {string[]} withoutUsages
 * @param {(string | number)[]} withoutOrgIds
 * @returns
 */
export const hasEntityInCurrentOrganization = (
  store,
  withTypes,
  withUsages,
  withoutUsages,
  withoutOrgIds,
) => {
  if (!store.currentUser || !store.currentUser.entities) return false;
  let entities = store.currentUser.entities.filter(
    (e) => e.__typename === 'Entity',
  );

  entities = entities.filter((e) => !withTypes || withTypes.includes(e.type));
  entities = entities.filter(
    (e) =>
      !withUsages ||
      !e.organization ||
      withUsages.includes(e.organization.usage),
  );
  entities = entities.filter(
    (e) =>
      !withoutUsages ||
      !e.organization ||
      !withoutUsages.includes(e.organization.usage),
  );
  entities = entities.filter(
    (e) =>
      !withoutOrgIds ||
      !e.organization ||
      !withoutOrgIds.includes(e.organization.id),
  );
  if (store.currentOrganization && store.currentOrganization.id) {
    entities = entities.filter(
      (e) => e.organization.id === store.currentOrganization.id,
    );
  }
  return entities.length > 0;
};

/**
 * Verify if it is a mobile or tablet device
 * @returns {boolean}
 */
export const isMobileOrTablet = () => {
  let check = false;
  try {
    if (navigator && window) {
      // @ts-ignore
      (function (a) {
        if (
          /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(
            a,
          ) ||
          /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
            a.substr(0, 4),
          )
        )
          check = true;
        // @ts-expect-error - Opera is a legacy property
      })(navigator.userAgent || navigator.vendor || window.opera);
    }
  } catch (e) {
    // continue regardless of error
  }
  return check;
};

/**
 * Verify if it is a enum included in array
 * @param {string[]} array
 * @param {string} enumName
 * @returns {boolean}
 */
export const isEnumIncluded = (array, enumName) => {
  if (array && enumName) {
    return (
      array.includes(enumName.toLowerCase()) ||
      array.includes(enumName.toUpperCase())
    );
  }
  return false;
};

/**
 * Verify if it is a IOS device
 * @returns {boolean}
 */
export const isIOSDevice = () =>
  [
    'iPad Simulator',
    'iPhone Simulator',
    'iPod Simulator',
    'iPad',
    'iPhone',
    'iPod',
  ].includes(navigator.platform) ||
  (navigator.userAgent.includes('Mac') && 'ontouchend' in document);

/**
 * Verify if it can submit message with recipients
 * @param {{ allRecipients: { selected: boolean }[], allGroups: { selected: boolean, entities: { nodes: { id: number } } }[] }} values
 * @param {{
 *   monthlyMessageQuota: number,
 *   monthlyMessagesCount: number
 * }} organization
 * @returns {boolean}
 */
export const canSubmitMessageWithRecipients = (values, organization) => {
  const hasQuota = organization && organization.monthlyMessageQuota;
  if (!hasQuota) return true;
  const availableMessages =
    organization.monthlyMessageQuota - organization.monthlyMessagesCount;
  // @ts-ignore
  const recipients = [];

  if (values.allGroups) {
    const getSelectedGroups = values.allGroups.filter(
      (group) => group.selected,
    );
    getSelectedGroups.map((group) => recipients.push(group.entities.nodes));
  }

  if (values.allRecipients) {
    const getSelectedRecipients = values.allRecipients.filter(
      (recipient) => recipient.selected,
    );
    getSelectedRecipients.map((entity) => recipients.push(entity));
  }

  // @ts-ignore
  const recipientsWithoutRepeat = uniq(flatten(recipients));

  const countStudentRecipients = recipientsWithoutRepeat.filter(
    (e) => e.type === 'STUDENT',
  ).length;

  return (
    availableMessages >= countStudentRecipients || countStudentRecipients === 0
  );
};
/**
 * Verify if has entities with type student in a list
 * @param {{ type: ('ADMIN'|'STAFF'|'STUDENT')}[]} entities
 * @returns {boolean}
 */
export const hasStudentInList = (entities) => {
  if (!entities) return false;

  let hasStudent = false;
  if (entities.filter((entity) => entity.type === 'STUDENT').length > 0)
    hasStudent = true;
  return hasStudent;
};

/**
 * Verify if has entities with type student in a group
 * @param {{entities:{ nodes:{ type: ('ADMIN'|'STAFF'|'STUDENT')}[]}}[]} groups
 * @returns {boolean}
 */
export const hasStudentInGroup = (groups) => {
  if (!groups) return false;

  let hasStudent = false;
  groups.forEach((group) => {
    if (group && group.entities && hasStudentInList(group.entities.nodes))
      hasStudent = true;
  });
  return hasStudent;
};

/**
 * Verify if it can submit message
 * @param {{ monthlyMessageQuota: number, monthlyMessagesCount: number }} organization
 * @returns {boolean}
 */
export const canSubmitMessage = (organization) => {
  const hasQuota = organization && organization.monthlyMessageQuota;
  if (!hasQuota) return true;
  const availableMessages =
    organization.monthlyMessageQuota - organization.monthlyMessagesCount;

  return availableMessages > 0;
};

/** Verify if type param is equal the entity store type
 * @param {{ entity: { type: ('ADMIN'|'STAFF'|'STUDENT')}, currentEntity: { type: ('ADMIN'|'STAFF'|'STUDENT') } }} store
 * @param {('ADMIN'|'STAFF'|'STUDENT')} type
 * @returns {boolean}
 */
export const isEntityType = (store, type) => {
  const entity = store.entity || store.currentEntity;
  if (!entity) return false;
  return entity.type === type;
};

/**
 * Format phone with country code
 * @param {string} phone
 * @param {boolean} haveCountryCode
 * @returns {string}
 */
export const formatPhone = (phone, haveCountryCode = false) => {
  const cleanPhone = phone.replace(/[^0-9]/g, '').substring(0, 13);

  if (cleanPhone.length <= 0) {
    return '';
  }
  if (cleanPhone.length >= 12 && haveCountryCode) {
    return (
      '(' +
      cleanPhone.substring(2, 4) +
      ') ' +
      cleanPhone.substring(4, 9) +
      '-' +
      cleanPhone.substring(9, 13)
    );
  }
  if (cleanPhone.length <= 2) {
    return '(' + cleanPhone;
  }
  if (cleanPhone.length <= 7) {
    return '(' + cleanPhone.substring(0, 2) + ') ' + cleanPhone.substring(2, 7);
  }
  return (
    '(' +
    cleanPhone.substring(0, 2) +
    ') ' +
    cleanPhone.substring(2, 7) +
    '-' +
    cleanPhone.substring(7, 11)
  );
};

/**
 * Render Text with Popup
 * @param {string} text
 * @param {number} maxSize
 * @returns {import('semantic-ui-react').PopupContentProps | string}
 */
export const renderLongText = (text = '', maxSize = 32) => {
  if (text.length <= maxSize) return text;

  return (
    <Popup
      trigger={<span>{text.substring(0, maxSize).trim() + '...'}</span>}
      content={text}
      hoverable
    />
  );
};

/**
 * Render abbreviated long text
 * @param {string} text
 * @param {number} maxSize
 * @returns {string}
 */
export const renderLongTextRaw = (text = '', maxSize = 32) => {
  if (text.length <= maxSize) return text;

  return text.substring(0, maxSize).trim() + '...';
};

/**
 * @param {{
 *  type: string,
 *  address: string,
 *  tags: any,
 *  user: any
 * }[]} addresses
 */
export const removeDuplicateAddresses = (addresses) => {
  /** @type {typeof addresses} */
  const filteredAddresses = [];

  /** @type {Set<string>} */
  const addedAddresses = new Set();

  addresses.forEach((address) => {
    if (!addedAddresses.has(address.address.toLowerCase())) {
      addedAddresses.add(address.address.toLowerCase());
      filteredAddresses.push(address);
    }
  });

  return filteredAddresses;
};

/**
 * Debounce function
 * @param {Function} fn
 * @param {number} delay
 * @returns {Function}
 */
export const debounce = (fn, delay = 250) => {
  // @ts-ignore
  let timer = null;
  // @ts-ignore
  return function (...args) {
    // @ts-ignore
    const context = this;
    // @ts-ignore
    clearTimeout(timer);
    timer = setTimeout(() => {
      fn.apply(context, args);
    }, delay);
  };
};

/**
 * Parse Universal Date Format
 * @param {Date} date
 * @returns {string}
 */
export const parseUniversalDateFormat = (date) => {
  return moment(date).format('MM/DD/YYYY HH:mm:ss');
};

/**
 * Parse scope to form
 * @typedef {{
 *  messages: { read: boolean, delete: boolean },
 *  reports: { read: boolean, createUpdate: boolean, delete: boolean },
 *  entities: { read: boolean, createUpdate: boolean, delete: boolean, contentApprover: boolean },
 *  dashboard: { read: boolean },
 *  integration: { read: boolean },
 *  payments: { read: boolean },
 *  accounts: { read: boolean, createUpdate: boolean, delete: boolean },
 *  forms: { read: boolean, createUpdate: boolean, delete: boolean },
 *  links: { read: boolean, createUpdate: boolean, delete: boolean },
 *  organization: { update: boolean },
 * }} ScopeForm
 * @param {string} scope
 *
 * @returns {ScopeForm}
 */
export const parseScopeToForm = (scope) => ({
  messages: {
    read: !!scope && !!scope.includes('READ_MESSAGE'),
    delete: !!scope && !!scope.includes('DELETE_MESSAGE'),
  },
  reports: {
    read: !!scope && !!scope.includes('READ_REPORT'),
    createUpdate: !!scope && !!scope.includes('CREATE_UPDATE_REPORT'),
    delete: !!scope && !!scope.includes('DELETE_REPORT'),
  },
  entities: {
    read:
      !!scope &&
      !!scope.includes('READ_ENTITY') &&
      !!scope.includes('READ_GROUP'),
    createUpdate:
      !!scope &&
      !!scope.includes('CREATE_UPDATE_ENTITY') &&
      !!scope.includes('CREATE_UPDATE_GROUP'),
    contentApprover: !!scope && !!scope.includes('CONTENT_APPROVER'),
    delete: !!scope && !!scope.includes('DELETE_GROUP'),
  },
  dashboard: {
    read: !!scope && !!scope.includes('READ_DASHBOARD'),
  },
  integration: {
    read: !!scope && !!scope.includes('READ_INTEGRATION'),
  },
  payments: {
    read: !!scope && !!scope.includes('READ_PAYMENT'),
  },
  accounts: {
    read: !!scope && !!scope.includes('READ_ACCOUNT'),
    createUpdate: !!scope && !!scope.includes('CREATE_UPDATE_ACCOUNT'),
    delete: !!scope && !!scope.includes('DELETE_ACCOUNT'),
  },
  organization: {
    update: !!scope && !!scope.includes('UPDATE_ORGANIZATION'),
  },
  forms: {
    read: !!scope && !!scope.includes('READ_FORM'),
    createUpdate: !!scope && !!scope.includes('CREATE_UPDATE_FORM'),
    delete: !!scope && !!scope.includes('DELETE_FORM'),
  },
  links: {
    read: !!scope && !!scope.includes('READ_LINK'),
    createUpdate: !!scope && !!scope.includes('CREATE_UPDATE_LINK'),
    delete: !!scope && !!scope.includes('DELETE_LINK'),
  },
});

/**
 * Format text translated by i18 using markers.
 * @param {{
 *  text: string
 *  props: object
 *  Wrapper: ({ children }: {children: any }) => JSX.Element
 * }} param
 * @returns
 */
export const parseFormattedTranslation = ({ text, props, Wrapper }) => {
  const splittedText = text.split('**');

  return splittedText.map((t, i) => {
    if (i % 2 === 0) return t;
    if (Wrapper) return <Wrapper>{t}</Wrapper>;
    return <span {...props}>{t}</span>;
  });
};

/**
 * Zero fill
 * @param {number} number
 * @param {number} width
 * @returns {string}
 */
export const zeroFill = (number, width) => {
  let countWidth = width;
  countWidth -= number.toString().length;
  if (countWidth > 0) {
    return (
      new Array(countWidth + (/\./.test(String(number)) ? 2 : 1)).join('0') +
      number
    );
  }
  return number + ''; // always return a string
};

/**
 * Return the env color
 * @param {string} env
 * @returns {string}
 */
export const envColor = (env) => {
  switch (env) {
    case 'stage':
      return 'orange';
    case 'internal':
      return 'purple';
    case 'local':
      return 'blue';
    case 'beta':
      return 'teal';
    default:
      return 'olive';
  }
};

/**
 * Get next working day of date
 * @param {moment.Moment} date
 * @returns {moment.Moment}
 */
export const getNextWorkingDayOfDate = (date) => {
  if (date.day() === 6) {
    // saturday
    return moment(date).add(2, 'days');
  }
  if (date.day() === 7) {
    // sunday
    return moment(date).add(1, 'days');
  }
  return date;
};

/**
 * Calculate Reply Time
 * @param {{
 *  directReplyTime: number
 *  forwardedReplyTime: number
 *  directReplyMessages: number
 *  forwardedReplyMessages: number
 * }} param
 * @returns {number | null}
 */
export const calculateReplyTime = ({
  directReplyTime = 0,
  forwardedReplyTime = 0,
  directReplyMessages = 0,
  forwardedReplyMessages = 0,
}) =>
  directReplyMessages + forwardedReplyMessages
    ? (directReplyTime + forwardedReplyTime) /
      (directReplyMessages + forwardedReplyMessages)
    : null;

/**
 * Handle direct reply time
 * @param {number} seconds
 * @param {boolean} complete
 * @param {boolean} showMinutes
 * @returns {string}
 */
export const handleDirectReplyTime = (
  seconds,
  complete = false,
  showMinutes = false,
) => {
  if (seconds === 0) return ''; // handle 0min

  if (showMinutes) {
    // aritmetics
    let hours = Math.floor(seconds / 3600);
    const minutes = Math.round((seconds % 3600) / 60);

    // return value
    if (minutes === 60) {
      hours++;
      return `${hours}h `;
    }
    if (hours >= 1) {
      if (minutes > 0) return `${hours}h ${minutes}min`;
      return `${hours}h`;
    }
    if (minutes === 0) return '1min';
    return `${minutes}min`;
  }

  if (seconds >= 604800) {
    if (complete) {
      const days = Math.floor(seconds / 86400);
      return `${days} ${__('days')}`;
    }
    return `+7 ${__('days')}`; // handle >= 168h
  }

  // aritmetics
  let days = Math.floor(seconds / 86400);
  let hours = Math.floor((seconds - days * 86400) / 3600);
  let minutes = Math.floor((seconds - days * 86400 - hours * 3600) / 60);
  seconds = seconds - days * 86400 - hours * 3600 - minutes * 60;

  // round
  if (
    days < 1 &&
    ((minutes >= 30 && hours >= 1) || (minutes >= 51 && hours === 0))
  )
    hours++;
  if (hours === 24) days++;

  // return value
  if (days > 1) return `${days} ${__('days')}`;
  if (days === 1) return `${days} ${__('day')}`;
  if (hours >= 1) return `${hours}h`;

  // round minutes
  minutes += seconds / 60;
  if (minutes < 5) return `${Math.ceil(minutes)}min`; // handle < 5 min
  if (minutes > 50) return '1h'; // handle > 50 and < 1h
  minutes = Math.ceil(minutes / 5) * 5;
  return `${minutes}min`;
};

/**
 * Has json structure
 * @param {string} string
 * @returns {boolean}
 */
export const hasJsonStructure = (string) => {
  if (typeof string !== 'string') return false;
  try {
    const result = JSON.parse(string);
    const type = Object.prototype.toString.call(result);
    return type === '[object Object]' || type === '[object Array]';
  } catch (err) {
    return false;
  }
};

/**
 * Clear snackbar
 * @param {{ snackbar: { active: boolean, message: string, success: boolean, dismissAfter: number }}} store
 */
export const clearSnackbar = (store) => {
  if (store.snackbar.active) {
    store.snackbar = {
      active: false,
      message: '',
      success: false,
      dismissAfter: 2000,
    };
  }
};

/**
 * Get integration organization id from URL
 * @param {{ pathname: string }} location
 * @returns {number | null}
 */
export const getIntegrationOrganizationIdFromURL = (location) =>
  Number(
    location.pathname.match(/organizations\/([0-9]+)([^0-9]|$)/) &&
      // @ts-ignore
      location.pathname.match(/organizations\/([0-9]+)([^0-9]|$)/)[1],
  ) || null;

/**
 * Get entity id from URL
 * @param {{ pathname: string }} location
 * @returns {number | null}
 */
export const getEntityIdFromURL = (location) =>
  Number(
    location.pathname.match(/entities\/([0-9]+)([^0-9]|$)/) &&
      // @ts-ignore
      location.pathname.match(/entities\/([0-9]+)([^0-9]|$)/)[1],
  ) || null;

/**
 *
 * @param {string[]} entityScopes
 * @returns {{ entityScopesArrived: string[], entityScopesRecurrence: string[], entityScopesEnrollment: string[], entityScopes: newEntityScopes }}
 */
export const splitPermissionsByFeature = (entityScopes) => {
  /**
   * @type {string[]}
   */
  const newEntityScopes = [];
  /**
   * @type {string[]}
   */
  const entityScopesArrived = [];
  /**
   * @type {string[]}
   */
  const entityScopesRecurrence = [];
  /**
   * @type {string[]}
   */
  const entityScopesEnrollment = [];

  entityScopes.forEach((item) => {
    if (
      (item.includes('READ_ENTITY') &&
        entityScopes.indexOf('READ_GROUP') === -1) ||
      item.includes('ARRIVAL')
    ) {
      entityScopesArrived.push(item);
    } else if (item.includes('RECURRENCE')) {
      entityScopesRecurrence.push(item);
    } else if (item.includes('ENROLLMENT')) {
      entityScopesEnrollment.push(item);
    } else {
      newEntityScopes.push(item);
    }
  });
  return {
    entityScopesArrived,
    entityScopesRecurrence,
    entityScopesEnrollment,
    entityScopes: newEntityScopes,
  };
};

/**
 * Return if user is approver based at the store
 * @param  {{
 *  store: {
 *    entity: {
 *      scope: string[],
 *      type: string,
 *    },
 *    currentOrganization: {
 *      unleashStatus: {
 *        approve_message: boolean
 *      },
 *      plan: string
 *    }
 *    currentUser: {
 *      isMaster: boolean,
 *    }
 *  }
 * }} store
 */
export const isMessageApprover = ({ store }) => {
  const featureEnabled =
    store.currentOrganization &&
    store.currentOrganization.unleashStatus &&
    store.currentOrganization.unleashStatus.approve_message &&
    store.currentOrganization.plan === 'PREMIUM';

  const userHasApprovalPermission = store?.entity?.scope?.some(
    (scopePermission) => scopePermission === 'CONTENT_APPROVER',
  );

  const isMasterUser =
    store?.entity?.type === 'ADMIN' || store?.currentUser?.isMaster;

  if ((userHasApprovalPermission || isMasterUser) && featureEnabled) {
    return true;
  }

  return false;
};

/**
 * Return if user is approval writer based at the store
 * @param  {{
 *  store: {
 *    entity: {
 *      policy: string[],
 *      type: string,
 *    },
 *    currentOrganization: {
 *      unleashStatus: {
 *        approve_message: boolean
 *      },
 *      plan: string
 *    }
 *    currentUser: {
 *      isMaster: boolean,
 *    }
 *  }
 * }} store
 */
export const isMessageWriter = ({ store }) => {
  const featureEnabled =
    store.currentOrganization &&
    store.currentOrganization.unleashStatus &&
    store.currentOrganization.unleashStatus.approve_message &&
    store.currentOrganization.plan === 'PREMIUM';

  const userHasWriterPermission = store?.entity?.policy?.some(
    (policyPermission) =>
      policyPermission === 'NEED_APPROVAL_MESSAGE' ||
      policyPermission === 'NEED_APPROVAL_REPLY',
  );

  const isMasterUser =
    store?.entity?.type === 'ADMIN' || store?.currentUser?.isMaster;

  if (userHasWriterPermission && !isMasterUser && featureEnabled) {
    return true;
  }

  return false;
};

/**
 * Get the URL with a hash in query params for removing browser local cache
 * @param {{ pathname: string }} url
 */
export const createUrlWithHash = (url) => {
  if (!url) return null;

  const BASE_36 = 36;
  const HASH_LENGTH = 13;
  const HASH_START_INDEX = 2;

  const hashString = Math.random()
    .toString(BASE_36)
    .substring(HASH_START_INDEX, HASH_LENGTH + HASH_START_INDEX);

  return `${url}?hash=${hashString}`;
};

/**
 * @param {{
 *  type?: string,
 *  initial?: null | string | number | (string | number | null | undefined)[],
 * }} options
 */
export function formatReportFieldInitial(options) {
  if (options.type !== 'SELECT' && options.type !== 'CHECK') {
    return options.initial;
  }

  if (options.initial === null || options.initial === undefined) {
    return options.type === 'CHECK' ? [] : options.initial;
  }

  const array = Array.isArray(options.initial)
    ? options.initial.map((value) =>
      value === null || value === undefined ? null : `${value}`,
    )
    : `${options.initial}`.split(',');

  return options.type === 'CHECK' ? array : [array[0]];
}

/**
 * @param {string} date
 */
export const formatInputDate = (date) => {
  const sanitizeDate = date.replace(/\D/g, '');

  if (sanitizeDate.length <= 2) {
    return sanitizeDate;
  } else if (sanitizeDate.length <= 4) {
    return `${sanitizeDate.slice(0, 2)}/${sanitizeDate.slice(2)}`;
  } else {
    return `${sanitizeDate.slice(0, 2)}/${sanitizeDate.slice(2, 4)}/${sanitizeDate.slice(4, 8)}`;
  }
};

/**
 * @param {string} CPF
 */
export const formatInputCPF = (CPF) => {
  const sanitizeCPF = CPF.replace(/\D/g, '');

  if (sanitizeCPF.length <= 3) {
    return sanitizeCPF;
  } else if (sanitizeCPF.length <= 6) {
    return `${sanitizeCPF.slice(0, 3)}.${sanitizeCPF.slice(3)}`;
  } else if (sanitizeCPF.length <= 9) {
    return `${sanitizeCPF.slice(0, 3)}.${sanitizeCPF.slice(3, 6)}.${sanitizeCPF.slice(6)}`;
  } else {
    return `${sanitizeCPF.slice(0, 3)}.${sanitizeCPF.slice(3, 6)}.${sanitizeCPF.slice(6, 9)}-${sanitizeCPF.slice(9)}`;
  }
};

/**
 * @param {string} val
 */
export function capFirstLetter(val) {
  return String(val).charAt(0).toUpperCase() + String(val).slice(1);
}

/**
 * @param {string} originalStack
 */
export function getStackFromGraphqlError(originalStack) {
  return originalStack.split('[')[1].split(']')[0];
}

/**
 * @param {*} errorStackMapper
 * @param {string} originalStack
 */
function checkIfStackExists(errorStackMapper, originalStack) {
  return Object.keys(errorStackMapper).some((key) =>
    originalStack.includes(key),
  );
}

/**
 * @param {{stack: string, message: string}} originalErrors
 */
export function getErrorForStudent(originalErrors) {
  if (!originalErrors.stack || !originalErrors.message) {
    return;
  }
  const originalStack = originalErrors.stack;
  const error = originalErrors.message;

  const errorStackMapper = {
    ErrorDuplicatedDocumentNumber: {
      modalError: {
        title: __('Document number Already exists'),
        message: __(
          'It is not possible to add the same document number to more than one different student. Confirm the added information.',
        ),
      },
      inputErrors: {
        documentNumber: __(error),
      },
    },
    ErrorDuplicatedEntity: {
      modalError: {
        title: __('Possible duplication identified'),
        message: __(
          'There is already another student with the same name, date of birth and/or document number. Confirm the added information or add a new document number for the student',
        ),
      },
      inputErrors: {
        entityDuplicatedError: __(error),
      },
    },
    ErrorDuplicatedEid: {
      modalError: {
        title: __('ID already exists'),
        message: __(
          'It is not possible to add the same ID to more than one different profile or channel. Confirm the added information',
        ),
      },
      inputErrors: {
        eid: __(error),
      },
    },
  };

  if (!checkIfStackExists(errorStackMapper, originalStack)) {
    return;
  }

  const stack = getStackFromGraphqlError(originalStack);
  // @ts-ignore
  const errorByStack = errorStackMapper[stack];
  if (errorByStack) {
    return {
      inputErrors: errorByStack.inputErrors,
      modalError: errorByStack.modalError,
    };
  }
  return;
}

/**
 * @param {string} url
 * @param {string} access_token
 * @param {string} filename
 */
export function handleExport(url, access_token, filename) {
  const xhr = new XMLHttpRequest();
  xhr.open('GET', url, true);
  xhr.setRequestHeader('Authorization', `Bearer ${access_token}`);
  xhr.responseType = 'blob';

  xhr.onload = function () {
    if (xhr.status === 200) {
      const a = document.createElement('a');
      const url = window.URL.createObjectURL(xhr.response);
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
    }
  };

  xhr.send();
}

/**
 * @param {string} inputString
 */
export function matchBase64Image(inputString) {
  const base64ImageRegex = /data:image\/[a-zA-Z]+;base64,[^\s'"]+/g;
  return inputString.match(base64ImageRegex);
}

/**
 * @param {string} inputString
 * @param {{
 *  access_token: string,
 *  url: string,
 *  uploadUrl: string,
 *  app: {
 *    url: string,
 *    uploadUrl: string,
 *  },
 *  entity: {
 *    id: string,
 *  }
 * }} store
 */
export async function processString(inputString, store) {
  const api = createAPI({
    access_token: store.access_token,
    url: store.app.url,
    uploadUrl: store.app.uploadUrl,
  });

  const matches = matchBase64Image(inputString);
  const uploadedUrls = [];

  if (matches) {
    for (let i = 0; i < matches.length; i++) {
      const base64Image = matches[i];

      // Extrair tipo MIME e dados base64
      const [header, base64Data] = base64Image.split(',');
      const mimeType = header?.match(/data:(.*);base64/)?.[1] ?? '';

      // Converter base64 para Blob
      const byteCharacters = atob(base64Data);
      const byteArrays = [];

      for (let offset = 0; offset < byteCharacters.length; offset += 512) {
        const slice = byteCharacters.slice(offset, offset + 512);
        const byteNumbers = new Array(slice.length);
        for (let j = 0; j < slice.length; j++) {
          byteNumbers[j] = slice.charCodeAt(j);
        }
        byteArrays.push(new Uint8Array(byteNumbers));
      }

      const blob = new Blob(byteArrays, { type: mimeType });
      const fileName = `image_${store.entity.id}_${Date.now()}.${mimeType.split('/')[1]}`;
      const file = new File([blob], fileName, { type: mimeType });

      /** @type {{errors: string[], data: {uploadMedia: {media: {id: string, filename: string, uri: string}}}}} */
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      const imageUrl = await api.upload('media', {
        temporary: true,
        file,
        query: `
          mutation UploadMedia ($entityId: Int!) {
            uploadMedia (input: {entityId: $entityId, transcode: true}) {
              media {
                id: dbId
                filename
                uri(size:"w1280", temporary: true)
              }
            }
          }`,
        variables: `{"entityId": ${store.entity.id}}`,
      });

      if (imageUrl.errors) {
        throw new Error(`Error uploading image: ${imageUrl.errors}`);
      }

      if (
        imageUrl &&
        imageUrl.data &&
        imageUrl.data.uploadMedia &&
        imageUrl.data.uploadMedia.media
      ) {
        const uri = imageUrl.data.uploadMedia.media.uri;
        uploadedUrls.push(uri);
      }
    }
  }

  return uploadedUrls;
}
